import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { createTheme as createLegacyTheme, ThemeProvider as LegacyThemeProvider } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import i18n from 'i18n'
import ruMui from 'localization/ru/Mui.json'
import kyMui from 'localization/ky/Mui.json'
import enMui from 'localization/en/Mui.json'
import { applyTheme, readTheme, saveTheme, THEME_STORAGE_KEY, themeUserKey } from './preferences'

Object.entries({ ru: ruMui, ky: kyMui, en: enMui }).forEach(([language, labels]) => i18n.addResourceBundle(language, 'Mui', labels, true, true))

const ThemeContext = createContext({ theme: 'light', setTheme: () => {}, toggleTheme: () => {} })

export const useAppTheme = () => useContext(ThemeContext)

export function getMaterialDefaultProps(t, language) {
  const formatNumber = value => new Intl.NumberFormat(language === 'en' ? 'en-GB' : language === 'ru' ? 'ru-RU' : 'ky-KG').format(value)
  const pageAriaLabel = type => t(({
    first: 'Перейти на первую страницу', last: 'Перейти на последнюю страницу',
    next: 'Перейти на следующую страницу', previous: 'Перейти на предыдущую страницу',
  })[type] || 'Пропущенные страницы')
  return {
    MuiAutocomplete: {
      clearText: t('Очистить'), closeText: t('Закрыть'), openText: t('Открыть'),
      loadingText: t('Загрузка…'), noOptionsText: t('Нет доступных вариантов'),
      getLimitTagsText: value => t('Ещё {{value}}', { value: formatNumber(value) }),
    },
    MuiBreadcrumbs: { expandText: t('Показать полный путь') },
    MuiAlert: { closeText: t('Закрыть') },
    MuiTablePagination: {
      labelRowsPerPage: t('Строк на странице:'),
      labelDisplayedRows: ({ from, to, count }) => t('{{from}}–{{to}} из {{total}}', {
        from: formatNumber(from), to: formatNumber(to),
        total: count === -1 ? t('более {{to}}', { to: formatNumber(to) }) : formatNumber(count),
      }),
      getItemAriaLabel: pageAriaLabel,
    },
    MuiPagination: {
      'aria-label': t('Навигация по страницам'),
      getItemAriaLabel: (type, page, selected) => type === 'page'
        ? t(selected ? 'Страница {{page}}' : 'Перейти на страницу {{page}}', { page: formatNumber(page) })
        : pageAriaLabel(type),
    },
    MuiRating: {
      emptyLabelText: t('Рейтинг отсутствует'),
      getLabelText: value => t('{{value}} звезда', { value: formatNumber(value), count: value }),
    },
    MuiCircularProgress: { 'aria-label': t('Загрузка') },
    MuiLinearProgress: { 'aria-label': t('Загрузка') },
  }
}

export default function ThemeProvider({ children }) {
  const { t, i18n: currentLanguage } = useTranslation('Mui')
  const language = currentLanguage.resolvedLanguage || currentLanguage.language || 'ky'
  const userId = useSelector(state => state.user?.userId)
  const [theme, updateTheme] = useState(() => readTheme(userId))

  useLayoutEffect(() => { updateTheme(readTheme(userId)) }, [userId])
  useLayoutEffect(() => { applyTheme(theme) }, [theme])
  useEffect(() => {
    const onStorage = event => {
      if (event.key === null || event.key === THEME_STORAGE_KEY || event.key === themeUserKey(userId)) updateTheme(readTheme(userId))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [userId])

  const setTheme = useCallback(value => updateTheme(saveTheme(value, userId)), [userId])
  const toggleTheme = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [setTheme, theme])
  const context = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme])
  const dark = theme === 'dark'
  const defaults = useMemo(() => getMaterialDefaultProps(t, language), [t, language])
  const palette = useMemo(() => ({
    mode: theme,
    primary: { main: dark ? '#aaa4ff' : '#0d00f3' },
    secondary: { main: dark ? '#f2a8a8' : '#da0000' },
    background: { default: dark ? '#292929' : '#f4f5fa', paper: dark ? '#333333' : '#ffffff' },
    text: { primary: dark ? '#f2f2f4' : '#3a3541', secondary: dark ? '#c1c5cd' : '#7c8291', disabled: dark ? '#9399a4' : '#a0a5b3' },
    divider: dark ? '#51535a' : '#e7e8f0',
    error: { main: dark ? '#ffaaa7' : '#b64242' },
    warning: { main: dark ? '#f0ce82' : '#9b7129' },
    success: { main: dark ? '#8dd9ae' : '#34784c' },
    info: { main: dark ? '#9dcafb' : '#377cce' },
  }), [dark, theme])
  const muiTheme = useMemo(() => createTheme({
    palette,
    typography: { fontFamily: "'Nunito', system-ui, sans-serif" },
    components: {
      ...Object.fromEntries(Object.entries(defaults).map(([name, defaultProps]) => [name, { defaultProps }])),
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiTableCell: { styleOverrides: { root: { borderColor: palette.divider } } },
      MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: dark ? '#eeeeef' : '#333333', color: dark ? '#242424' : '#ffffff' } } },
    },
  }), [palette, dark, defaults])
  const legacyTheme = useMemo(() => createLegacyTheme({
    palette: { ...palette, type: theme },
    typography: { fontFamily: "'Nunito', system-ui, sans-serif" },
    props: {
      ...defaults,
      MuiTablePagination: {
        labelRowsPerPage: defaults.MuiTablePagination.labelRowsPerPage,
        labelDisplayedRows: defaults.MuiTablePagination.labelDisplayedRows,
        backIconButtonText: defaults.MuiTablePagination.getItemAriaLabel('previous'),
        nextIconButtonText: defaults.MuiTablePagination.getItemAriaLabel('next'),
      },
    },
    overrides: { MuiPaper: { root: { backgroundImage: 'none' } } },
  }), [palette, theme, defaults])

  return <ThemeContext.Provider value={context}><LegacyThemeProvider theme={legacyTheme}><MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider></LegacyThemeProvider></ThemeContext.Provider>
}
